import { PaginationControls } from "@/components/pagination-controls";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  useCoaches,
  useDeleteCoach,
  useInviteCoach,
  useRestoreCoach,
  useRestoreDeletedCoach,
  useSuspendCoach,
} from "@/hooks/useCoaches";
import { useDebounce } from "@/hooks/useDebounce";
import { useRequestPasswordReset } from "@/hooks/useInternalUsers";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { NAME_ERROR, NAME_REGEX } from "@/lib/validation";
import { Coach, CoachStatus, CoachType } from "@/lib/schema";
import { formatDistanceToNow } from "date-fns";
import {
  Eye,
  KeyRound,
  MoreHorizontal,
  Plus,
  Search,
  Trash2,
  UserCheck,
  Users as UsersIcon,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { useLocation } from "wouter";

const coachTypeLabels: Record<CoachType, string> = {
  coach: "Coach",
  guest: "Guest Contributor",
  verified_coach: "Verified Coach",
};

const statusLabels: Record<CoachStatus, string> = {
  active: "Active",
  suspended: "Suspended",
  deactivated: "Deactivated",
};

function getStatusBadgeVariant(
  status: CoachStatus,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "suspended":
      return "destructive";
    case "deactivated":
      return "secondary";
    default:
      return "outline";
  }
}

export default function Coaches() {
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [actionDialog, setActionDialog] = useState<{
    type:
      | "suspend"
      | "restore"
      | "restoreDeleted"
      | "delete"
      | "resetPassword"
      | null;
    coach: Coach | null;
  }>({ type: null, coach: null });
  const [actionReason, setActionReason] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const { toast } = useToast();

  const userRoles = user?.roles;
  const canCreate =
    !!userRoles && hasPermission(userRoles, "coaches", "create");
  const canSuspend =
    !!userRoles && hasPermission(userRoles, "coaches", "suspend");
  const canRestore =
    !!userRoles && hasPermission(userRoles, "coaches", "restore");
  const canDelete =
    !!userRoles && hasPermission(userRoles, "coaches", "delete");
  const canResetPassword =
    !!userRoles &&
    hasPermission(userRoles, "coaches", "trigger_password_reset");

  const debouncedSearch = useDebounce(searchQuery, 300);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const { data: coachesResponse, isLoading } = useCoaches({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
  });

  const createMutation = useInviteCoach();
  const suspendMutation = useSuspendCoach();
  const restoreMutation = useRestoreCoach();
  const restoreDeletedMutation = useRestoreDeletedCoach();
  const deleteMutation = useDeleteCoach();
  const resetPasswordMutation = useRequestPasswordReset();

  const coaches = coachesResponse?.coaches || [];

  const handleCreateSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const name = (formData.get("name") as string).trim();
    if (!NAME_REGEX.test(name)) {
      toast({ title: NAME_ERROR, variant: "destructive" });
      return;
    }

    const data = {
      name,
      email: formData.get("email") as string,
      coachType: formData.get("coachType") as CoachType,
    };

    createMutation.mutate(data, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
      },
    });
  };

  const handleAction = () => {
    if (!actionDialog.coach) return;

    if (actionDialog.type === "suspend") {
      if (!actionReason.trim()) {
        toast({ title: "Reason is required", variant: "destructive" });
        return;
      }
      suspendMutation.mutate(
        {
          id: actionDialog.coach.id,
          reason: actionReason,
        },
        {
          onSuccess: () => {
            setActionDialog({ type: null, coach: null });
            setActionReason("");
          },
        },
      );
    } else if (actionDialog.type === "restore") {
      restoreMutation.mutate(actionDialog.coach.id, {
        onSuccess: () => {
          setActionDialog({ type: null, coach: null });
        },
      });
    } else if (actionDialog.type === "restoreDeleted") {
      restoreDeletedMutation.mutate(actionDialog.coach.id, {
        onSuccess: () => {
          setActionDialog({ type: null, coach: null });
        },
      });
    } else if (actionDialog.type === "delete") {
      if (!actionReason.trim()) {
        toast({ title: "Reason is required", variant: "destructive" });
        return;
      }
      deleteMutation.mutate(
        { id: actionDialog.coach.id, reason: actionReason },
        {
          onSuccess: () => {
            setActionDialog({ type: null, coach: null });
            setActionReason("");
          },
        },
      );
    } else if (actionDialog.type === "resetPassword") {
      if (!actionDialog.coach.email) return;
      resetPasswordMutation.mutate(
        { email: actionDialog.coach.email },
        {
          onSuccess: () => {
            setActionDialog({ type: null, coach: null });
          },
        },
      );
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-background">
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            Coaches
          </h1>
          <p className="text-muted-foreground mt-1">
            The compassionate voices that nurture your community's growth
          </p>
        </div>

        <section>
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h2 className="text-xl font-serif font-medium text-foreground">
                All Coaches
              </h2>
              <p className="text-sm text-muted-foreground">
                {coachesResponse?.pagination.total || 0}{" "}
                {(coachesResponse?.pagination.total || 0) === 1
                  ? "guide"
                  : "guides"}{" "}
                in your team
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search coaches..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10 w-64"
                  data-testid="input-search-coaches"
                />
              </div>
              <Select
                value={statusFilter}
                onValueChange={handleStatusFilterChange}
              >
                <SelectTrigger
                  className="w-[140px]"
                  data-testid="select-coach-status"
                >
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="deactivated">Deactivated</SelectItem>
                </SelectContent>
              </Select>
              {canCreate && (
                <Button
                  onClick={() => setIsCreateDialogOpen(true)}
                  data-testid="button-add-coach"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Invite Coach
                </Button>
              )}
            </div>
          </div>

          {isLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {coaches?.length === 0 ? (
                <div className="text-center py-12">
                  <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">
                    {searchQuery || statusFilter !== "all"
                      ? "No coaches found matching your filters"
                      : "Invite your first coach to get started"}
                  </p>
                </div>
              ) : (
                coaches?.map((coach) => {
                  const isDeactivated = coach.status === "deactivated";
                  return (
                  <Card
                    key={coach.id}
                    className={`transition-all duration-300 ${
                      isDeactivated
                        ? "opacity-60"
                        : "hover-elevate cursor-pointer"
                    }`}
                    onClick={
                      isDeactivated
                        ? undefined
                        : () => setLocation(`/coaches/${coach.id}/profile`)
                    }
                    data-testid={`row-coach-${coach.id}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-4 min-w-0">
                          <Avatar className="h-10 w-10 shrink-0">
                            {coach.photoUrl && (
                              <AvatarImage src={coach.photoUrl} />
                            )}
                            <AvatarFallback className="bg-muted text-muted-foreground">
                              {coach.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")
                                .slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {coach.name}
                            </p>
                            <p className="text-sm text-muted-foreground truncate">
                              {coach.expertise?.slice(0, 2).join(", ") ||
                                "No expertise listed"}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6 flex-wrap">
                          <Badge
                            variant={getStatusBadgeVariant(
                              coach.status as CoachStatus,
                            )}
                          >
                            {statusLabels[coach.status as CoachStatus]}
                          </Badge>
                          <div className="text-left w-[110px] hidden sm:block">
                            <p className="text-xs text-muted-foreground">
                              Type
                            </p>
                            <p className="text-sm text-foreground truncate">
                              {coachTypeLabels[coach.coachType as CoachType] ||
                                "Coach"}
                            </p>
                          </div>
                          <div className="text-left w-[90px] hidden sm:block">
                            <p className="text-xs text-muted-foreground">
                              Content
                            </p>
                            <p className="text-sm text-foreground">
                              {coach.publishedCount || 0} published
                            </p>
                          </div>
                          <div className="text-left w-[80px] hidden md:block">
                            <p className="text-xs text-muted-foreground">
                              Languages
                            </p>
                            <div className="flex gap-1">
                              {coach.languages?.slice(0, 2).map((lang) => (
                                <span
                                  key={lang}
                                  className="text-xs text-foreground"
                                >
                                  {lang.toUpperCase()}
                                </span>
                              ))}
                              {(coach.languages?.length || 0) > 2 && (
                                <span className="text-xs text-muted-foreground">
                                  +{(coach.languages?.length || 0) - 2}
                                </span>
                              )}
                              {!coach.languages?.length && (
                                <span className="text-xs text-muted-foreground">
                                  -
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-left w-[100px] hidden lg:block">
                            <p className="text-xs text-muted-foreground">
                              Last Active
                            </p>
                            <p className="text-sm text-foreground truncate">
                              {coach.lastActiveAt
                                ? formatDistanceToNow(
                                    new Date(coach.lastActiveAt),
                                    { addSuffix: true },
                                  )
                                : "Never"}
                            </p>
                          </div>
                          <div onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`button-coach-actions-${coach.id}`}
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-48">
                                {!isDeactivated && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setLocation(
                                        `/coaches/${coach.id}/profile`,
                                      )
                                    }
                                    data-testid={`button-view-coach-${coach.id}`}
                                  >
                                    <Eye className="mr-2 h-4 w-4" />
                                    View Profile
                                  </DropdownMenuItem>
                                )}
                                {canResetPassword && coach.email && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setActionDialog({
                                        type: "resetPassword",
                                        coach,
                                      })
                                    }
                                    data-testid={`button-reset-password-coach-${coach.id}`}
                                  >
                                    <KeyRound className="mr-2 h-4 w-4" />
                                    Reset Password
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {coach.status === "active" && canSuspend && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setActionDialog({
                                        type: "suspend",
                                        coach,
                                      })
                                    }
                                    className="text-destructive"
                                    data-testid={`button-suspend-coach-${coach.id}`}
                                  >
                                    <UserX className="mr-2 h-4 w-4" />
                                    Suspend
                                  </DropdownMenuItem>
                                )}
                                {coach.status === "suspended" && canRestore && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setActionDialog({
                                        type: "restore",
                                        coach,
                                      })
                                    }
                                    data-testid={`button-restore-coach-${coach.id}`}
                                  >
                                    <UserCheck className="mr-2 h-4 w-4" />
                                    Restore
                                  </DropdownMenuItem>
                                )}
                                {coach.status === "deactivated" &&
                                  canRestore && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        setActionDialog({
                                          type: "restoreDeleted",
                                          coach,
                                        })
                                      }
                                      data-testid={`button-restore-deleted-coach-${coach.id}`}
                                    >
                                      <UserCheck className="mr-2 h-4 w-4" />
                                      Restore
                                    </DropdownMenuItem>
                                  )}
                                {coach.status !== "deactivated" &&
                                  canDelete && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        setActionDialog({
                                          type: "delete",
                                          coach,
                                        })
                                      }
                                      className="text-destructive"
                                      data-testid={`button-delete-coach-${coach.id}`}
                                    >
                                      <Trash2 className="mr-2 h-4 w-4" />
                                      Delete
                                    </DropdownMenuItem>
                                  )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  );
                })
              )}
            </div>
          )}

          {coachesResponse?.pagination && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={coachesResponse.pagination.totalPages}
              totalItems={coachesResponse.pagination.total}
              startIndex={(currentPage - 1) * pageSize}
              endIndex={Math.min(
                currentPage * pageSize,
                coachesResponse.pagination.total,
              )}
              canGoPrev={currentPage > 1}
              canGoNext={currentPage < coachesResponse.pagination.totalPages}
              onPageChange={setCurrentPage}
              onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              onNext={() =>
                setCurrentPage((prev) =>
                  Math.min(coachesResponse.pagination.totalPages, prev + 1),
                )
              }
            />
          )}
        </section>
      </div>

      {/* Create Coach Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Invite New Coach</DialogTitle>
            <DialogDescription>
              Create a new coach profile. They will receive an invite to set up
              their account.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                name="name"
                required
                data-testid="input-coach-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                placeholder="coach@example.com"
                data-testid="input-coach-email"
              />
              <p className="text-xs text-muted-foreground">
                An invitation will be sent to this email address
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coachType">Coach Type</Label>
              <Select name="coachType" defaultValue="coach">
                <SelectTrigger data-testid="select-coach-type-form">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="verified_coach">Verified Coach</SelectItem>
                  <SelectItem value="guest">Guest Contributor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending}
                data-testid="button-submit-coach"
              >
                {createMutation.isPending ? "Creating..." : "Invite Coach"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Action Confirmation Dialog */}
      <Dialog
        open={actionDialog.type !== null}
        onOpenChange={(open) =>
          !open && setActionDialog({ type: null, coach: null })
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === "suspend" && "Suspend Coach"}
              {actionDialog.type === "restore" && "Restore Coach"}
              {actionDialog.type === "restoreDeleted" &&
                "Restore Deleted Coach"}
              {actionDialog.type === "delete" && "Delete Coach"}
              {actionDialog.type === "resetPassword" && "Reset Password"}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === "suspend" &&
                `This will suspend ${actionDialog.coach?.name}'s account. They will not be able to access the platform.`}
              {actionDialog.type === "restore" &&
                `This will restore ${actionDialog.coach?.name}'s account access.`}
              {actionDialog.type === "restoreDeleted" &&
                `This will restore ${actionDialog.coach?.name}'s deleted account. The coach will be visible again in the system.`}
              {actionDialog.type === "delete" &&
                `This will soft delete ${actionDialog.coach?.name}'s profile. The coach will be hidden but can be restored later.`}
              {actionDialog.type === "resetPassword" &&
                `A password reset email will be sent to ${actionDialog.coach?.email}. You will not see the new password.`}
            </DialogDescription>
          </DialogHeader>
          {(actionDialog.type === "suspend" ||
            actionDialog.type === "delete") && (
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (required)</Label>
              <Textarea
                id="reason"
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={
                  actionDialog.type === "suspend"
                    ? "Enter reason for suspension..."
                    : "Enter reason for deletion..."
                }
                data-testid={
                  actionDialog.type === "suspend"
                    ? "input-suspend-reason"
                    : "input-delete-reason"
                }
              />
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog({ type: null, coach: null });
                setActionReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant={
                actionDialog.type === "restore" ||
                actionDialog.type === "restoreDeleted" ||
                actionDialog.type === "resetPassword"
                  ? "default"
                  : "destructive"
              }
              onClick={handleAction}
              disabled={
                suspendMutation.isPending ||
                restoreMutation.isPending ||
                restoreDeletedMutation.isPending ||
                deleteMutation.isPending ||
                resetPasswordMutation.isPending
              }
              data-testid="button-confirm-action"
            >
              {suspendMutation.isPending ||
              restoreMutation.isPending ||
              restoreDeletedMutation.isPending ||
              deleteMutation.isPending ||
              resetPasswordMutation.isPending
                ? "Processing..."
                : actionDialog.type === "suspend"
                  ? "Suspend"
                  : actionDialog.type === "restore"
                    ? "Restore"
                    : actionDialog.type === "restoreDeleted"
                      ? "Restore"
                      : actionDialog.type === "resetPassword"
                        ? "Send Reset Email"
                        : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
